// Oberflächen-Einstellungen des Nutzers aus users.settings (Design-Runde 2): Dichte (kompakt/bequem) und Countdown des
// Power Dialers. Einmal je Sitzung geholt, Änderungen erscheinen sofort und werden im Hintergrund gespeichert.
// Die Dichte steht außerdem im Browser (localStorage), damit die Seite beim Laden nicht flackert.
import { useEffect, useSyncExternalStore } from "react";
import { oberflaecheApi } from "../api";
import type { Dichte, OberflaechenEinstellungen } from "../api";
import { beimNutzerwechsel } from "./sitzung";

const DICHTE_SCHLUESSEL = "crm-dichte";

export const STANDARD_COUNTDOWN = 10;

interface Stand {
  geladen: boolean;
  dichte: Dichte;
  countdown: number;
}

function dichteAusBrowser(): Dichte {
  try {
    return localStorage.getItem(DICHTE_SCHLUESSEL) === "bequem" ? "bequem" : "kompakt";
  } catch {
    return "kompakt";
  }
}

let stand: Stand = { geladen: false, dichte: dichteAusBrowser(), countdown: STANDARD_COUNTDOWN };
try {
  document.documentElement.setAttribute("data-dichte", stand.dichte);
} catch {
  /* kein Dokument (Test) */
}
let ladend: Promise<void> | null = null;
const hoerer = new Set<() => void>();

function setze(neu: Stand): void {
  stand = neu;
  try {
    document.documentElement.setAttribute("data-dichte", neu.dichte);
    localStorage.setItem(DICHTE_SCHLUESSEL, neu.dichte);
  } catch {
    /* egal */
  }
  hoerer.forEach((h) => h());
}

function ausServer(e: OberflaechenEinstellungen): Stand {
  return { geladen: true, dichte: e.dichte === "bequem" ? "bequem" : "kompakt", countdown: typeof e.dialer_countdown === "number" ? e.dialer_countdown : STANDARD_COUNTDOWN };
}

export function oberflaecheLaden(): Promise<void> {
  if (stand.geladen) return Promise.resolve();
  if (!ladend) {
    ladend = oberflaecheApi
      .lesen()
      .then((r) => setze(ausServer(r.settings)))
      .catch(() => undefined)
      .finally(() => {
        ladend = null;
      });
  }
  return ladend;
}

beimNutzerwechsel(() => {
  ladend = null;
  setze({ geladen: false, dichte: dichteAusBrowser(), countdown: STANDARD_COUNTDOWN });
});

export function oberflaecheAendern(teil: { dichte?: Dichte; dialer_countdown?: number }): void {
  setze({
    ...stand,
    ...(teil.dichte ? { dichte: teil.dichte } : {}),
    ...(teil.dialer_countdown !== undefined ? { countdown: teil.dialer_countdown } : {}),
  });
  oberflaecheApi.aendern(teil).catch(() => undefined);
}

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => hoerer.delete(h);
}

export function useOberflaeche(): Stand & { aendern: typeof oberflaecheAendern } {
  const s = useSyncExternalStore(abonnieren, () => stand, () => stand);
  useEffect(() => {
    void oberflaecheLaden();
  }, []);
  return { ...s, aendern: oberflaecheAendern };
}
